import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import "@/components/landing/landing.css";

/** Both demos run on one 20-second clock, so the tenant's application reaches the agent's dashboard on cue. */
export const LOOP_MS = 20000;
/** Both demos fade out just before the loop restarts, so the reset is never seen. */
export const FADE_OUT = 19300;
const TICK_MS = 50;

export const prefersReducedMotion = () => typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/** Milliseconds into the loop, in 50ms ticks. `still` freezes it (reduced motion). Pauses with the tab, because rAF does. */
export const useLoop = (still) => {
  const [t, setT] = useState(still ?? 0);
  useEffect(() => {
    if (still != null) {
      setT(still);
      return undefined;
    }
    let frame;
    let last = performance.now();
    let elapsed = 0;
    const tick = (now) => {
      elapsed = (elapsed + Math.min(100, now - last)) % LOOP_MS;
      last = now;
      const q = Math.floor(elapsed / TICK_MS) * TICK_MS;
      setT((prev) => (prev === q ? prev : q));
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [still]);
  return t;
};

export const between = (t, from, to) => t >= from && t < to;

/** Typewriter: how much of `text` has been typed `t` ms into the loop, starting at `from`. */
export const typed = (text, t, from, msPerChar = 38) => text.slice(0, Math.max(0, Math.floor((t - from) / msPerChar)));

/** The demos are real-size layouts shrunk to fit their column, so they look exactly like the app at any width. */
export const Scaled = ({ width, height, className, children }) => {
  const ref = useRef(null);
  const [scale, setScale] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    setScale(el.clientWidth / width);
    const observer = new ResizeObserver(([entry]) => setScale(entry.contentRect.width / width));
    observer.observe(el);
    return () => observer.disconnect();
  }, [width]);
  return (
    <div ref={ref} className={cn("relative w-full overflow-hidden", className)} style={{ height: height * scale }}>
      <div className="absolute left-0 top-0 origin-top-left" style={{ width, height, transform: `scale(${scale})` }}>{children}</div>
    </div>
  );
};

/** A ripple where the demo "taps" a button. Sits inside a `relative overflow-hidden` element and takes its text colour. */
export const Tap = ({ on }) => (on ? <span className="landin-tap" /> : null);

export const Caret = () => <span className="landin-caret" />;
